export const EVIDENCE_SOURCES = [
  ["email", "Email"],
  ["whatsapp", "WhatsApp screenshot or export"],
  ["pdf", "PDF"],
  ["photograph", "Photograph"],
  ["screenshot", "Screenshot"],
  ["scanned_physical", "Scanned physical quotation"],
  ["supplier_representative", "Supplier representative"],
  ["ecommerce_listing", "Ecommerce listing"],
  ["online_marketplace", "Online marketplace"],
  ["website_catalogue", "Website catalogue"],
  ["budgetary_estimate", "Budgetary estimate"],
  ["proforma_estimate", "Proforma estimate"],
  ["verbal_reference", "Verbal reference"],
  ["other", "Other recorded source"],
];

export const EVIDENCE_CLASSIFICATIONS = [
  ["buyer_recorded_quotation", "Buyer-recorded quotation"],
  ["buyer_recorded_awaiting_verification", "Buyer-recorded quotation awaiting verification"],
  ["market_price_reference", "Market-price reference"],
  ["budgetary_quotation", "Budgetary quotation"],
  ["proforma_quotation", "Proforma quotation"],
  ["verbal_unverified_reference", "Verbal / unverified reference"],
  ["quotation_amendment", "Quotation amendment"],
  ["received_after_deadline", "Received after deadline"],
];

const LABELS = {
  supplier_submitted_quotation: "Supplier submitted",
  buyer_recorded_quotation: "Buyer recorded",
  supplier_confirmed_quotation: "Supplier confirmed",
  buyer_recorded_awaiting_verification: "Verification pending",
  market_price_reference: "Market reference",
  budgetary_quotation: "Budgetary only",
  proforma_quotation: "Proforma",
  verbal_unverified_reference: "Unverified reference",
  quotation_amendment: "Amended",
  received_after_deadline: "Late submission",
  rejected_evidence: "Rejected",
  supplier_submitted: "Supplier submitted",
  buyer_recorded: "Buyer recorded",
  supplier_confirmed: "Supplier confirmed",
  pending: "Verification pending",
  admitted: "Admitted to comparison",
  rejected: "Rejected",
  supplier_confirmation_requested: "Supplier confirmation pending",
  supplier_disputed: "Supplier disputed",
  market_reference: "Market reference",
  reference_only: "Reference only",
  excluded: "Excluded from comparison",
};

export function evidenceClassificationForSource(source, selected) {
  if (["ecommerce_listing", "online_marketplace", "website_catalogue"].includes(source))
    return "market_price_reference";
  if (source === "verbal_reference") return "verbal_unverified_reference";
  if (
    ["whatsapp", "photograph", "screenshot", "scanned_physical"].includes(source) &&
    selected === "buyer_recorded_quotation"
  )
    return "buyer_recorded_awaiting_verification";
  return selected;
}

export function provenanceBadges(record = {}) {
  const values = [
    record.provenance,
    record.classification,
    record.verification_state,
    record.comparison_state,
  ].filter(Boolean);
  if (record.version_number > 1 && !values.includes("quotation_amendment"))
    values.push("quotation_amendment");
  if (record.late_submission && !values.includes("received_after_deadline"))
    values.push("received_after_deadline");
  return [...new Set(values)].map((value) => ({
    value,
    label: LABELS[value] || String(value).replaceAll("_", " "),
    tone:
      value === "rejected_evidence" || value === "rejected" || value === "supplier_disputed"
        ? "error"
        : value === "admitted" || value === "supplier_confirmed"
          ? "success"
          : ["market_price_reference", "market_reference", "reference_only"].includes(value)
            ? "info"
            : "warning",
  }));
}

export function marketplaceWarning(record = {}) {
  if (
    ["ecommerce_listing", "online_marketplace", "website_catalogue"].includes(
      record.source_channel,
    ) ||
    record.classification === "market_price_reference"
  )
    return "Marketplace price, seller, availability, freight, tax and delivery may change. This record is a benchmark, not a formal supplier bid.";
  return null;
}

export function quotationEvidenceCoverage(record = {}) {
  const status = (value, partial = false) =>
    value ? (partial ? "Partially available" : "Available") : "Not recorded";
  return [
    { category: "Original immutable file", status: status(record.original_sha256) },
    { category: "Source and sender provenance", status: status(record.source_channel && (record.sender_email || record.sender_phone || record.physical_source), !record.sender_email && !record.sender_phone) },
    { category: "Extraction result", status: record.extraction_state === "unavailable" ? "Unsupported by current integration" : status(record.extracted_fields && Object.keys(record.extracted_fields).length) },
    { category: "Manual corrections", status: status(record.corrections?.length) },
    { category: "Verification decision", status: status(record.decisions?.length) },
    { category: "Supplier confirmation", status: record.supplier_confirmation_state === "configuration_required" ? "Configuration required" : status(["confirmed", "disputed"].includes(record.supplier_confirmation_state)) },
    { category: "Amendment ancestry", status: status(record.parent_evidence_id || record.version_number > 1) },
    { category: "Integrity flags", status: status(record.integrity_flags?.length) },
    { category: "Comparison admission", status: status(record.comparison_state) },
  ];
}

export function differsFromExtraction(extracted = {}, recorded = {}) {
  return Object.keys(extracted)
    .filter((key) => JSON.stringify(extracted[key]) !== JSON.stringify(recorded[key]))
    .map((key) => ({
      field_name: key,
      extracted_value: extracted[key],
      corrected_value: recorded[key],
      reason: "Human correction against original evidence",
    }));
}

export const INTEGRITY_LANGUAGE = {
  potential_duplicate: "Potential duplicate",
  identity_mismatch: "Identity mismatch",
  manual_extraction_difference: "Review required",
  late_evidence: "Late evidence",
  claimed_date_mismatch: "Claimed date mismatch",
  segregation_of_duties: "Segregation-of-duties concern",
  shared_contact: "Shared supplier contact",
  missing_reference: "Missing quotation reference",
  price_variance: "Price variance",
  l1_exception: "L1 exception",
};

